import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

const TEACHER_ENDPOINT = {
  TEACHERS: "/teachers",
}

export type TeacherListItem = {
  _id: string
  firstName: string
  lastName?: string
  title?: string | null
  email: string
  phoneNumber: string
  status: "active" | "inactive"
  role: { _id: string; name: string }
  primarySubject?: { _id: string; title: string; courseIdentifier: string } | null
  primaryStage?: string | null
  employeeId?: string | null
  classTeacherCount: number
}

export type TeacherAssignment = {
  _id: string
  teacher: string
  session: { _id: string; name?: string; title?: string }
  branch: { _id: string; name: string }
  class: { _id: string; name: string }
  section: { _id: string; name: string }
  course: { _id: string; title: string; courseIdentifier: string }
}

export type TeacherProfile = TeacherListItem & {
  accessibleBranches?: string[]
  campusAccess: { _id: string; name: string }[]
  homeBranch?: { _id: string; name: string }
  employee?: { employeeID?: string; department?: string; designation?: string } | null
  assignments: TeacherAssignment[]
  classTeacherSections: {
    _id: string
    name: string
    branch?: { _id: string; name: string }
    session?: { _id: string; name?: string; title?: string }
    className?: string | null
  }[]
}

const getAllTeachers = async (params?: { q?: string; role?: string; campus?: string }) => {
  const response = await apiClient({
    url: appendQueryParams(TEACHER_ENDPOINT.TEACHERS, params || {}),
    method: "GET",
  })
  return response.data
}

const getTeacherById = async (id: string) => {
  const response = await apiClient({
    url: `${TEACHER_ENDPOINT.TEACHERS}/${id}`,
    method: "GET",
  })
  return response.data
}

export type TeacherCreateData = {
  email: string
  firstName: string
  lastName?: string
  title?: string
  phoneNumber: string
  password: string
  role: string
  status?: "active" | "inactive"
  primarySubject?: string | null
  primaryStage?: string | null
}

export type TeacherUpdateData = Partial<Omit<TeacherCreateData, "email" | "password">> & {
  password?: string
}

const createTeacher = async (data: TeacherCreateData) => {
  const response = await apiClient({
    url: TEACHER_ENDPOINT.TEACHERS,
    method: "POST",
    data,
  })
  return response.data
}

const updateTeacher = async (id: string, data: TeacherUpdateData) => {
  const response = await apiClient({
    url: `${TEACHER_ENDPOINT.TEACHERS}/${id}`,
    method: "PUT",
    data,
  })
  return response.data
}

const deactivateTeacher = async (id: string) => {
  const response = await apiClient({
    url: `${TEACHER_ENDPOINT.TEACHERS}/${id}`,
    method: "DELETE",
  })
  return response.data
}

const setCampusAccess = async (id: string, campusIds: string[]) => {
  const response = await apiClient({
    url: `${TEACHER_ENDPOINT.TEACHERS}/${id}/campus-access`,
    method: "PUT",
    data: { campusIds },
  })
  return response.data
}

const getClassSubjects = async (params: { session: string; class: string; section?: string; branch?: string }) => {
  const response = await apiClient({
    url: appendQueryParams(`${TEACHER_ENDPOINT.TEACHERS}/class-subjects`, params),
    method: "GET",
  })
  return response.data
}

const addAssignment = async (
  id: string,
  data: { session: string; branch: string; class: string; section: string; course: string }
) => {
  const response = await apiClient({
    url: `${TEACHER_ENDPOINT.TEACHERS}/${id}/assignments`,
    method: "POST",
    data,
  })
  return response.data
}

const removeAssignment = async (id: string, assignmentId: string) => {
  const response = await apiClient({
    url: `${TEACHER_ENDPOINT.TEACHERS}/${id}/assignments/${assignmentId}`,
    method: "DELETE",
  })
  return response.data
}

const setClassTeacherOf = async (id: string, sectionIds: string[]) => {
  const response = await apiClient({
    url: `${TEACHER_ENDPOINT.TEACHERS}/${id}/class-teacher-of`,
    method: "PUT",
    data: { sectionIds },
  })
  return response.data
}

export {
  getAllTeachers,
  getTeacherById,
  createTeacher,
  updateTeacher,
  deactivateTeacher,
  setCampusAccess,
  getClassSubjects,
  addAssignment,
  removeAssignment,
  setClassTeacherOf,
}
